import { useCallback, useEffect, useMemo, useState } from "react";
import { useDialogo } from "./dialogo";
import "./paneles.css";
import { SeoDelCliente, type KeywordObjetivo } from "./SeoDelCliente.js";
import { tomarPedido } from "./ir-al-cliente.js";

/**
 * Qué sabe BilboBot, y de quién.
 *
 * La memoria existía desde el principio pero sólo se podía escribir hablando:
 * el director guardaba lo que le parecía y no había forma de comprobarlo. Un
 * dato equivocado sobre un cliente se repite en cada conversación y se vuelve
 * verdad a fuerza de repetirse.
 *
 * La pantalla se ordena en cuatro alturas, de lo general a lo concreto:
 *
 *   1. Los números que piden actuar.
 *   2. Las categorías, como pestañas: es la capa que se elige primero.
 *   3. Lo que sabe, a la izquierda y con sitio para leerse.
 *   4. De quién, a la derecha: el cliente y lo que hay que revisar.
 *
 * El cliente se elige con un desplegable y no con una lista fija de 58 filas.
 * Una lista de ese tamaño ocupa toda la pantalla para ofrecer algo que se usa
 * una vez cada rato, y empuja hacia abajo justo lo que se viene a leer.
 */

interface Memoria {
  id: string;
  titulo: string;
  contenido: string;
  tipo: string;
  origen: string;
  ambito: string;
  etiquetas: string[];
  confianza: number;
  caducaEn: string | null;
  verificadaEn: string | null;
  actualizadaEn: string;
  clienteId: string | null;
  categoriaId: string | null;
  cliente: string | null;
  caducada: boolean;
  /** De qué va: finanzas, SEO, diseño… */
  area: string;
  /** Si sigue siendo verdad, o cuánto hace que nadie lo dice. */
  frescura: "reciente" | "conviene-confirmar" | "vieja" | "sin-verificar";
  diasSinConfirmar: number | null;
  frase: string;
}

/**
 * Lo que sabe del cliente que no está en la tabla de memorias.
 *
 * Vive en la ficha —otra tabla— pero responde a la misma pregunta. Enseñar
 * sólo las memorias hacía parecer que no sabía nada de quince clientes de los
 * que sabe el dominio, las keywords y qué tienen contratado.
 */
interface Ficha {
  dominio: string | null;
  otrosDominios: string[];
  paginasPilares: {
    url: string;
    keywords: {
      texto: string;
      posicionInicial?: number | null;
      desde?: string;
    }[];
    nota?: string;
  }[];
  /**
   * Las keywords objetivo, en dos listas que no se mezclan.
   *
   * Juntas mienten en el resultado: cuarenta de blog posicionando bien tapan
   * que las cinco de servicio —las que pagan— están en la página tres.
   */
  deServicio: KeywordObjetivo[];
  informativas: KeywordObjetivo[];
  estrategiaDesde: string | null;
  estrategiasAnteriores: {
    texto: string;
    desde: string;
    hasta: string | null;
  }[];
  estrategia: string | null;
  planDeContenidos: {
    titulo: string;
    keyword?: string;
    mes?: string;
    estado?: "previsto" | "escrito" | "publicado";
    /** El encargo para quien lo escriba. */
    notas?: string;
  }[];
  estadoSeo: string | null;
  seRanking: string | null;
  searchConsole: string | null;
  sitemap: string | null;
  analytics: string | null;
  keywords: string[];
  email: string | null;
  alias: string[];
  servicios: { tipo: string; descripcion: string; conIva: number | null }[];
  /** El cliente-grupo al que pertenece, si es una de sus sociedades (26/10/2026). */
  grupo: string | null;
  credenciales: {
    nombre: string;
    usuario: string | null;
    referencia: string | null;
    valida: boolean;
  }[];
}

interface ClienteConFicha {
  id: string;
  nombre: string;
  cuantas: number;
  ficha: Ficha;
}

interface Area {
  slug: string;
  nombre: string;
  ayuda: string;
  id: string;
  ambito: string;
  /** Las de fábrica se renombran, pero no se quitan. */
  deFabrica: boolean;
}

interface Resumen {
  total: number;
  clientesConMemoria: number;
  clientesSinNada: number;
  categorias: number;
  porRevisar: number;
  caducadas: number;
}

interface Datos {
  areas: Area[];
  memorias: Memoria[];
  clientes: ClienteConFicha[];
  sinCliente: number;
  resumen: Resumen;
}

const TIPOS: Record<string, string> = {
  fact: "dato",
  preference: "preferencia",
  hypothesis: "hipótesis",
  procedure: "procedimiento",
  episode: "episodio",
};

const ORIGENES: Record<string, string> = {
  owner: "lo dijiste tú",
  user: "un usuario",
  email: "un correo",
  trello: "Trello",
  worker: "un ordenador",
  document: "un documento",
  api: "una API",
  inference: "lo dedujo",
  system: "el sistema",
};

/**
 * Un icono por categoría.
 *
 * No decoran: son lo que deja recorrer una lista de treinta con la vista en
 * vez de leyendo. Si una categoría nueva no tiene el suyo, se usa uno neutro
 * — que aparezca sin icono sería peor que uno genérico.
 */
const ICONOS: Record<string, string> = {
  finanzas: "€",
  seo: "↗",
  diseno: "◆",
  web: "◉",
  cliente: "◐",
  trato: "★",
  otros: "•",
  identidad: "◆",
  limites: "⊘",
  personas: "☺",
  agencia: "⌂",
  decisiones: "⚖",
  procedimientos: "≡",
  objetivos: "◎",
  lecciones: "✎",
};

/**
 * Lo que no es de ningún cliente son DOS cosas, no una.
 *
 * Estaba todo bajo «La agencia» y ahí acababan mezcladas dos memorias que no
 * se parecen en nada: «BilboBot no toca las webs de clientes» y «Luis es
 * empleado de Bilbolink». Una dice cómo se comporta el sistema y la otra es un
 * dato de la empresa.
 *
 * Y hay una tercera que NO va aquí: Bilbolink como cliente —su web, su SEO, su
 * Analytics— vive en su ficha con las mismas categorías que cualquier otro,
 * porque a esos efectos es un cliente más. Lo dijo Jorge: «una cosa es
 * BilboBot y otra es Bilbolink, que es como otro cliente aunque sea mi
 * agencia».
 *
 * Cuál es cuál se sabe por el ÁMBITO DE SU CATEGORÍA (`sistema` o `casa`), no
 * por el de la memoria: las treinta y cinco están guardadas como `global` y no
 * hacía falta moverlas de sitio para poder separarlas.
 */
const BILBOBOT = "__bilbobot";
const CASA = "__casa";
/** El valor viejo. Se sigue entendiendo para no romper enlaces guardados. */
const SIN_CLIENTE = "__general";
const NUEVO_CLIENTE = "__nuevo";
const TODAS = "__todas";
/**
 * El SEO tiene pantalla propia y sale SOLO en su categoria.
 *
 * Estaba pintandose con cualquier categoria elegida —bastaba con haber elegido
 * cliente—, asi que entrabas en Finanzas o en Diseno y te comias el panel de
 * SEO entero antes de llegar a lo que ibas a mirar.
 */
const SEO = "seo";
/**
 * Las tareas del mes no son una categoría de memoria: son trabajo hecho.
 *
 * Comparten pestaña con las categorías porque comparten pregunta —«¿qué hay de
 * este cliente?»— y porque tenerlas en otra pantalla obligaría a recordar en
 * cuál de las dos está cada cosa.
 */
const TAREAS = "__tareas";
/**
 * Publicaciones no es una carpeta de recuerdos: es el plan editorial.
 *
 * Como las tareas del mes, ocupa el ancho entero y no lleva la columna de
 * recuerdos al lado. Es una cola de trabajo que se lee de izquierda a derecha.
 */
const PUBLICACIONES = "publicaciones";

/**
 * Los dos grupos de clientes (Jorge, 26/10/2026): «los importantes, que son los
 * que pagan mensualmente SEO o mantenimiento, y los anuales, que sólo pagan
 * alojamiento».
 *
 * Sale de lo que ya tiene apuntado cada uno en «Lo contratado» (`tipo` mensual
 * o anual): no hay que marcarlo aparte, y si un cliente contrata SEO cambia de
 * grupo solo. Uno sin nada apuntado va aparte: no es anual, es que no se sabe.
 */
export type GrupoDeCliente = "mensual" | "anual" | "sin";

export function grupoDelCliente(servicios: { tipo: string }[]): GrupoDeCliente {
  if (servicios.some((x) => /mensual/i.test(x.tipo))) return "mensual";
  if (servicios.length > 0) return "anual";
  return "sin";
}

/**
 * Lo que paga un cliente-grupo: lo suyo más lo de sus sociedades (26/10/2026).
 * Grupo Scanner no tiene nada a su nombre; pagan los seis centros, cada uno lo suyo.
 */
export function serviciosConGrupo(c: { id: string; ficha: { servicios: { tipo: string; descripcion: string; conIva: number | null }[] } }, todos: { ficha: { grupo: string | null; servicios: { tipo: string; descripcion: string; conIva: number | null }[] } }[]) {
  return [...c.ficha.servicios, ...todos.filter((o) => o.ficha.grupo === c.id).flatMap((o) => o.ficha.servicios)];
}

export const GRUPOS: { clave: GrupoDeCliente; nombre: string }[] = [
  { clave: "mensual", nombre: "Importantes — pagan cada mes (SEO o mantenimiento)" },
  { clave: "anual", nombre: "Anuales — sólo alojamiento" },
  { clave: "sin", nombre: "Sin nada contratado apuntado" },
];

function cuando(fecha: string): string {
  const dias = Math.floor(
    (Date.now() - new Date(fecha).getTime()) / 86_400_000,
  );
  if (dias === 0) return "hoy";
  if (dias === 1) return "ayer";
  if (dias < 30) return `hace ${dias} días`;
  if (dias < 365) return `hace ${Math.floor(dias / 30)} meses`;
  return `hace ${Math.floor(dias / 365)} años`;
}

export function MemoriaPanel({ irA }: { irA?: (vista: "mando" | "contenidos") => void } = {}) {
  const { preguntar, pedir, avisar, dialogo } = useDialogo();
  const [datos, setDatos] = useState<Datos | null>(null);
  /* Si se llega desde «Mando» con un cliente y un sitio pedidos, se abre ahí. */
  const [pedido] = useState(() => tomarPedido());
  const [cliente, setCliente] = useState<string>(pedido?.clienteId ?? "todos");
  const [categoria, setCategoria] = useState<string>(pedido ? SEO : TODAS);
  const [busca, setBusca] = useState("");
  const [editando, setEditando] = useState<Memoria | null>(null);
  const [creando, setCreando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  /*
   * Cuántas se pintan y cuáles se leen enteras (22/10/2026).
   *
   * Pintaba las 391 enteras: la pantalla medía 302.000 píxeles y no había forma
   * de llegar a la de abajo. Ahora cuarenta cada vez, cada una en tres líneas, y
   * se abre la que se quiere leer.
   */
  const [cuantasSeVen, setCuantasSeVen] = useState(40);
  const [enteras, setEnteras] = useState<Set<string>>(() => new Set());
  const alternarEntera = (id: string) =>
    setEnteras((antes) => {
      const despues = new Set(antes);
      if (despues.has(id)) despues.delete(id);
      else despues.add(id);
      return despues;
    });

  async function cargar() {
    const r = await fetch("/api/memoria", { credentials: "same-origin" });
    if (r.ok) setDatos((await r.json()) as Datos);
  }

  useEffect(() => {
    void cargar();
  }, []);

  /**
   * De qué ámbito es cada categoría, por su slug.
   *
   * Se construye del listado que manda el servidor, no de una lista escrita
   * aquí: Jorge puede crear categorías desde la pantalla y una lista fija en el
   * navegador dejaría las nuevas sin ámbito al día siguiente.
   */
  const ambitoDe = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of datos?.areas ?? []) m.set(a.slug, a.ambito);
    return m;
  }, [datos]);

  /**
   * Si una memoria pertenece a la vista elegida.
   *
   * Las de BilboBot y las de la casa no se distinguen por el ámbito de la
   * memoria —todas son `global`— sino por el de su categoría. Una sin
   * clasificar no es de ninguna de las dos, y se enseña en las dos para que no
   * desaparezca: una memoria que no se ve en ninguna parte es peor que una mal
   * colocada.
   */
  const esDeLaVista = useCallback(
    (memoriaArea: string, tieneCliente: boolean, vista: string) => {
      if (tieneCliente) return false;
      const ambito = ambitoDe.get(memoriaArea);
      if (!ambito || ambito === "empresa") return true;
      return vista === BILBOBOT ? ambito === "sistema" : ambito === "casa";
    },
    [ambitoDe],
  );

  /**
   * Qué categorías se enseñan como pestañas.
   *
   * Dependen de lo que se esté mirando, y eso es la mitad de la separación:
   * «Lo que no puede hacer» no pinta nada mirando la ficha de un cliente, y
   * «Hosting y correo» no pinta nada mirando quién es BilboBot. Enseñarlas
   * todas siempre es lo que hacía que nadie encontrara la que buscaba.
   *
   * Las de ámbito `empresa` salen en todas las vistas: son el cajón común —
   * «Sin clasificar» entre ellas— y esconderlas dejaría memorias sin pestaña
   * donde aparecer.
   */
  const pestanas = useMemo(() => {
    const areas = datos?.areas ?? [];
    if (cliente === BILBOBOT)
      return areas.filter(
        (a) => a.ambito === "sistema" || a.ambito === "empresa",
      );
    if (cliente === CASA)
      return areas.filter((a) => a.ambito === "casa" || a.ambito === "empresa");
    // Mirando un cliente o todos: lo suyo, sin lo de BilboBot ni lo de la casa.
    return areas.filter((a) => a.ambito !== "sistema" && a.ambito !== "casa");
  }, [datos, cliente]);

  /** Lo que pasa los tres filtros: cliente, categoría y búsqueda. */
  const visibles = useMemo(() => {
    if (!datos) return [];
    const texto = busca.trim().toLowerCase();

    return datos.memorias.filter((m) => {
      if (cliente === BILBOBOT || cliente === CASA)
        return (
          esDeLaVista(m.area, Boolean(m.clienteId), cliente) &&
          (categoria === TODAS || m.area === categoria) &&
          (!texto ||
            m.titulo.toLowerCase().includes(texto) ||
            m.contenido.toLowerCase().includes(texto) ||
            m.etiquetas.some((e) => e.toLowerCase().includes(texto)))
        );
      if (cliente === SIN_CLIENTE && m.clienteId) return false;
      if (
        cliente !== "todos" &&
        cliente !== SIN_CLIENTE &&
        m.clienteId !== cliente
      )
        return false;
      if (categoria !== TODAS && m.area !== categoria) return false;
      if (!texto) return true;
      return (
        m.titulo.toLowerCase().includes(texto) ||
        m.contenido.toLowerCase().includes(texto) ||
        (m.cliente ?? "").toLowerCase().includes(texto) ||
        m.etiquetas.some((e) => e.toLowerCase().includes(texto))
      );
    });
  }, [datos, cliente, categoria, busca]);

  /* Al cambiar lo que se mira, se vuelve a empezar por las cuarenta primeras. */
  useEffect(() => setCuantasSeVen(40), [cliente, categoria, busca]);

  /**
   * Cuántas hay en cada categoría, contando ya el cliente elegido.
   *
   * Si las pestañas enseñaran el total de siempre, elegir un cliente dejaría
   * pestañas con números que no corresponden a nada de lo que se ve.
   */
  const porCategoria = useMemo(() => {
    if (!datos) return new Map<string, number>();
    const cuenta = new Map<string, number>();
    for (const m of datos.memorias) {
      if (cliente === BILBOBOT || cliente === CASA) {
        if (!esDeLaVista(m.area, Boolean(m.clienteId), cliente)) continue;
      } else {
        if (cliente === SIN_CLIENTE && m.clienteId) continue;
        if (
          cliente !== "todos" &&
          cliente !== SIN_CLIENTE &&
          m.clienteId !== cliente
        )
          continue;
      }
      cuenta.set(m.area, (cuenta.get(m.area) ?? 0) + 1);
    }
    return cuenta;
  }, [datos, cliente]);

  const elegido =
    cliente !== "todos" && cliente !== SIN_CLIENTE
      ? (datos?.clientes.find((c) => c.id === cliente) ?? null)
      : null;

  /**
   * Dar de alta un cliente sin salir de la memoria.
   *
   * Los 58 de la cartera vienen de Holded, y eso dejaba fuera al que aún no
   * factura: el que acaba de llamar, el presupuesto en marcha, el colaborador.
   * De ninguno se podía guardar nada porque no había ficha donde colgarlo, y
   * la única salida era anotarlo en el cliente equivocado.
   */
  async function crearCliente() {
    const nombre = await pedir(
      "¿Cómo se llama el cliente nuevo?\n\nTal y como quieras verlo escrito en los avisos y en el chat.",
      "",
    );
    if (!nombre?.trim()) return;

    setOcupado(true);
    const alta = async (aunAsi: boolean) =>
      fetch("/api/memoria/clientes", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ nombre: nombre.trim(), aunAsi }),
      });

    let r = await alta(false);
    let cuerpo = (await r.json()) as {
      id?: string;
      error?: string;
      parecidos?: { id: string; nombre: string }[];
      puedesInsistir?: boolean;
    };

    /**
     * Lo parecido se pregunta, no se bloquea ni se crea a la ligera.
     *
     * Que "Aroka" acabe conviviendo con "CLINICA AROKA" parte la memoria en
     * dos mitades que no saben la una de la otra — justo el fallo que esta
     * pantalla existe para evitar. Pero sí pueden ser dos clientes distintos,
     * y quien lo sabe eres tú: por eso se enseña y se decide.
     */
    if (!r.ok && cuerpo.puedesInsistir && cuerpo.parecidos?.length) {
      const seguir = await preguntar(
        `${cuerpo.error}\n\n¿«${nombre.trim()}» es un cliente distinto?\n\n` +
          "Aceptar lo crea aparte. Cancelar no crea nada, y así puedes elegir el que ya existe en la lista.",
      );
      if (!seguir) {
        setOcupado(false);
        return;
      }
      r = await alta(true);
      cuerpo = (await r.json()) as typeof cuerpo;
    }

    if (!r.ok) {
      await avisar(cuerpo.error ?? "No se pudo crear.");
      // Si ya existía, al menos te deja delante del que existe.
      if (cuerpo.id) setCliente(cuerpo.id);
      setOcupado(false);
      return;
    }

    await cargar();
    // Te deja dentro del cliente recién creado: es lo que ibas a hacer.
    if (cuerpo.id) setCliente(cuerpo.id);
    setOcupado(false);
  }

  async function crearCategoria() {
    const nombre = await pedir(
      "¿Cómo se llama la categoría nueva?\n\nPor ejemplo: Competidores, Google Ads, Reuniones.",
      "",
    );
    if (!nombre?.trim()) return;

    setOcupado(true);
    const r = await fetch("/api/memoria/categorias", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ nombre: nombre.trim(), ambito: "empresa" }),
    });
    if (!r.ok)
      await avisar(
        ((await r.json()) as { error?: string }).error ?? "No se pudo crear.",
      );
    await cargar();
    setOcupado(false);
  }

  async function renombrarCategoria(area: Area) {
    const nombre = await pedir(
      `Nuevo nombre para «${area.nombre}»`,
      area.nombre,
    );
    if (!nombre?.trim() || nombre.trim() === area.nombre) return;

    setOcupado(true);
    await fetch(`/api/memoria/categorias/${area.id}`, {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ nombre: nombre.trim() }),
    });
    await cargar();
    setOcupado(false);
  }

  async function quitarCategoria(area: Area, cuantas: number) {
    const aviso =
      cuantas > 0
        ? `Se quita «${area.nombre}».\n\nSus ${cuantas} memorias NO se borran: se quedan sin clasificar.`
        : `Se quita «${area.nombre}». Está vacía.`;
    if (!await preguntar(aviso)) return;

    setOcupado(true);
    const r = await fetch(`/api/memoria/categorias/${area.id}`, {
      method: "DELETE",
      credentials: "same-origin",
    });
    if (!r.ok)
      await avisar(
        ((await r.json()) as { error?: string }).error ?? "No se pudo quitar.",
      );
    setCategoria(TODAS);
    await cargar();
    setOcupado(false);
  }

  /** "Esto sigue siendo verdad", sin tocar el contenido. */
  async function confirmar(m: Memoria) {
    setOcupado(true);
    await fetch(`/api/memoria/${m.id}`, {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ confirmar: true }),
    });
    await cargar();
    setOcupado(false);
  }

  async function olvidar(m: Memoria) {
    const motivo = await pedir(
      `¿Por qué se olvida «${m.titulo}»?\n\nNo se borra: se marca inactiva y queda el motivo.`,
      "ya no es cierto",
    );
    if (motivo === null) return;

    setOcupado(true);
    await fetch(`/api/memoria/${m.id}`, {
      method: "DELETE",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ motivo }),
    });
    await cargar();
    setOcupado(false);
  }

  if (!datos) return <div className="provisional">Cargando…</div>;

  const activa = datos.areas.find((a) => a.slug === categoria) ?? null;
  const porRevisar = datos.memorias.filter(
    (m) =>
      m.frescura === "vieja" || m.frescura === "sin-verificar" || m.caducada,
  );

  /**
   * La lista de recuerdos y la barra lateral, guardadas para poder colocarlas.
   *
   * Fuera del SEO van donde siempre, en dos columnas debajo de las pestanas de
   * categoria. Dentro del SEO se meten en la seccion «Tareas», porque ahi
   * colgaban sueltas al final de la pantalla: entrabas en SEO, mirabas el
   * panel, y debajo aparecia una tirada de notas sin apartado propio.
   *
   * Se quedan aqui como constantes en vez de salir a un componente porque usan
   * medio estado de esta pantalla —lo que se esta editando, lo ocupado que
   * esta, la categoria activa—, y sacarlas obligaria a pasar quince props.
   */
  const listaDeRecuerdos = (enSeo: boolean) => (
    <section className="caja">
      <header>
        {/*
         * Dentro del SEO el titulo ya lo pone la seccion.
         *
         * Repetir «SEO · BILBOLINK» dentro de la pestana «Tareas» del panel
         * de SEO de Bilbolink lo dice tres veces y no anade nada: ahi lo util
         * es saber que son las notas.
         */}
        <h2>
          {enSeo
            ? "Notas y pendientes"
            : activa
              ? activa.nombre
              : "Todo lo que sabe"}
          {!enSeo && elegido && <span className="de"> · {elegido.nombre}</span>}
        </h2>

        {/* Renombrar y quitar viven aquí, donde estás cuando te das cuenta
            de que la categoría se llama mal. */}
        {activa && !enSeo && (
          <span className="mandos-capa">
            <button
              onClick={() => void renombrarCategoria(activa)}
              disabled={ocupado}
            >
              renombrar
            </button>
            {!activa.deFabrica && (
              <button
                onClick={() => void quitarCategoria(activa, visibles.length)}
                disabled={ocupado}
              >
                quitar
              </button>
            )}
          </span>
        )}
      </header>

      {visibles.length === 0 && (
        <div className="hueco">
          <p>
            {busca
              ? "Nada que coincida con eso."
              : elegido
                ? `Nadie le ha contado nada sobre ${elegido.nombre}${activa ? ` en ${activa.nombre}` : ""}.`
                : "Aquí todavía no hay nada."}
          </p>

          {/**
           * El botón, donde estás mirando.
           *
           * El de la portada existe, pero cuando llegas a una categoría
           * vacía tienes los ojos AQUÍ. Un hueco que dice "no hay nada" y
           * no ofrece ponerlo es un callejón sin salida.
           */}
          {!busca && (
            <button
              className="acc"
              onClick={() => setCreando(true)}
              disabled={ocupado}
            >
              {elegido
                ? `Añadir lo primero sobre ${elegido.nombre}`
                : "Añadir la primera"}
            </button>
          )}
        </div>
      )}

      {visibles.slice(0, cuantasSeVen).map((m) => (
        <article
          className={m.caducada ? "recuerdo caducado" : "recuerdo"}
          key={m.id}
        >
          <span className="marca-tipo" aria-hidden="true">
            {ICONOS[m.area] ?? "•"}
          </span>

          <div className="txt">
            <div className="t">
              {m.titulo}
              {m.caducada && <span className="pill mal">caducada</span>}
              {m.frescura === "vieja" && (
                <span className="pill mal" title={m.frase}>
                  sin confirmar hace más de un año
                </span>
              )}
              {m.frescura === "conviene-confirmar" && (
                <span className="pill aviso-pill" title={m.frase}>
                  conviene confirmar
                </span>
              )}
              {m.frescura === "sin-verificar" && (
                <span
                  className="pill"
                  title="Nadie ha confirmado nunca este dato"
                >
                  sin verificar
                </span>
              )}
            </div>

            <div className={enteras.has(m.id) ? "cuerpo" : "cuerpo plegado"}>{m.contenido}</div>
            {m.contenido.length > 280 && (
              <button className="leer-entera" onClick={() => alternarEntera(m.id)} aria-expanded={enteras.has(m.id)}>
                {enteras.has(m.id) ? "Plegar" : "Leer entera"}
              </button>
            )}

            <div className="s">
              {[
                ...(elegido ? [] : [m.cliente ?? "la agencia"]),
                TIPOS[m.tipo] ?? m.tipo,
                ORIGENES[m.origen] ?? m.origen,
                cuando(m.actualizadaEn),
              ].join(" · ")}
              {m.etiquetas
                .filter((e) => !e.startsWith("area:"))
                .map((e) => (
                  <span className="pill" key={e}>
                    {e}
                  </span>
                ))}
            </div>
          </div>

          <div className="botones">
            {m.frescura !== "reciente" && (
              <button
                className="acc sec"
                onClick={() => void confirmar(m)}
                disabled={ocupado}
                title="Marcarla como comprobada hoy. No cambia el contenido."
              >
                Sigue valiendo
              </button>
            )}
            <button
              className="acc sec"
              onClick={() => setEditando(m)}
              disabled={ocupado}
            >
              Corregir
            </button>
            <button
              className="acc sec"
              onClick={() => void olvidar(m)}
              disabled={ocupado}
            >
              Olvidar
            </button>
          </div>
        </article>
      ))}

      {visibles.length > cuantasSeVen && (
        <div className="mas-recuerdos">
          <span>
            Se ven {cuantasSeVen} de {visibles.length}
          </span>
          <button className="leer-entera" onClick={() => setCuantasSeVen((n) => n + 40)}>
            Ver {Math.min(40, visibles.length - cuantasSeVen)} más
          </button>
        </div>
      )}
    </section>
  );

  const barraLateral = (
    <aside className="lado">
      {/* En SEO la ficha vive arriba, repartida en las secciones «Cliente»
          y «Configuracion». Aqui sale solo cuando no estas en SEO: en las
          dos a la vez obligaria a mirar cual de las dos es la buena, y no
          tenerla en ninguna dejaria las demas categorias sin saber de quien
          se esta hablando. */}
      {elegido && categoria !== SEO && (
        <>
          <QuienEs cliente={elegido} todos={datos.clientes} irA={setCliente} />
          <ComoEstaConectado cliente={elegido} />
        </>
      )}

      <div className="caja">
        <header>
          <h2>Lo que hay que revisar</h2>
        </header>

        {porRevisar.length === 0 ? (
          <p className="vacio2">
            Nada pendiente: todo lo que sabe se ha confirmado en los últimos
            meses.
          </p>
        ) : (
          <>
            {porRevisar.slice(0, 8).map((m) => (
              <button
                key={m.id}
                className="a-revisar"
                onClick={() => {
                  setCliente(m.clienteId ?? SIN_CLIENTE);
                  setCategoria(m.area);
                  setBusca(m.titulo.slice(0, 30));
                }}
              >
                <span className="t">{m.titulo}</span>
                <span className="s">
                  {m.caducada ? "caducada" : m.frase} ·{" "}
                  {m.cliente ?? "la agencia"}
                </span>
              </button>
            ))}
            {porRevisar.length > 8 && (
              <p className="vacio2">…y {porRevisar.length - 8} más.</p>
            )}
          </>
        )}
      </div>

      {/* Clientes de los que no sabe nada. Es la lista de trabajo real:
          un cliente sin datos es uno sobre el que responderá a ciegas. */}
      {datos.resumen.clientesSinNada > 0 && (
        <div className="caja">
          <header>
            <h2>
              No sabe nada de
              <span className="pill mal">{datos.resumen.clientesSinNada}</span>
            </h2>
          </header>
          {datos.clientes
            .filter((c) => c.cuantas === 0)
            .slice(0, 10)
            .map((c) => (
              <button
                key={c.id}
                className="a-revisar"
                onClick={() => setCliente(c.id)}
              >
                <span className="t">{c.nombre}</span>
                <span className="s">
                  {c.ficha.dominio ? c.ficha.dominio : "sin web guardada"}
                </span>
              </button>
            ))}
        </div>
      )}
    </aside>
  );

  return (
    <>
      {dialogo}
    <div className="memoria pnl-memoria">
      {/* Si se llegó desde «Mando», se vuelve a él con un clic, y fijo arriba aunque se baje. */}
      {pedido && irA && (
        <div className="volver-a-mando">
          <button type="button" className="acc sec" onClick={() => irA(pedido.desde)}>
            ← Volver a {pedido.desde === "contenidos" ? "Contenidos" : "Mando"}
          </button>
        </div>
      )}
      {/* ── Portada ── */}
      <header className="portada">
        <div className="que-es">
          <h1>Memoria</h1>
          <p>Todo lo que sabe BILBOBOT, organizado y listo para corregir.</p>
        </div>

        <div className="mandos">
          <input
            className="buscar"
            placeholder="Buscar en la memoria…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          {/**
           * Crear el cliente desde el propio desplegable.
           *
           * Es donde estás cuando descubres que no está: lo abres, recorres la
           * lista buscándolo y no aparece. Mandarte a otra pantalla en ese
           * momento es perder lo que ibas a guardar.
           */}
          <select
            className="elegir-cliente"
            value={cliente}
            disabled={ocupado}
            onChange={(e) => {
              if (e.target.value === NUEVO_CLIENTE) void crearCliente();
              else setCliente(e.target.value);
            }}
          >
            <option value="todos">
              Todos los clientes ({datos.memorias.length})
            </option>
            {/*
             * Dos entradas y no una: BilboBot y la casa son cosas distintas.
             * Bilbolink COMO CLIENTE está más abajo, en la lista de clientes,
             * con su web y su SEO como cualquier otro.
             */}
            <option value={BILBOBOT}>BilboBot — cómo es y qué no hace</option>
            <option value={CASA}>Bilbolink — cómo funciona la casa</option>
            {GRUPOS.map((g) => {
              /* Las sociedades de un grupo van debajo de su grupo, no sueltas. */
              const cabezas = datos.clientes.filter(
                (c) => !c.ficha.grupo && grupoDelCliente(serviciosConGrupo(c, datos.clientes)) === g.clave,
              );
              if (cabezas.length === 0) return null;
              return (
                <optgroup key={g.clave} label={`${g.nombre} (${cabezas.length})`}>
                  {cabezas.flatMap((c) => [
                    <option key={c.id} value={c.id}>
                      {c.nombre} ({c.cuantas})
                    </option>,
                    ...datos.clientes
                      .filter((m) => m.ficha.grupo === c.id)
                      .map((m) => (
                        <option key={m.id} value={m.id}>
                          {"\u00a0\u00a0\u00a0↳ "}
                          {m.nombre} ({m.cuantas})
                        </option>
                      )),
                  ])}
                </optgroup>
              );
            })}
            <option value={NUEVO_CLIENTE}>＋ Cliente nuevo…</option>
          </select>
          <button
            className="acc"
            onClick={() => setCreando(true)}
            disabled={ocupado}
          >
            + Nueva memoria
          </button>
        </div>
      </header>

      {/* ── Los números que piden actuar ── */}
      <div className="cifras">
        <div className="cifra">
          <span className="n">{datos.resumen.total}</span>
          <span className="q">recuerdos</span>
        </div>
        <div className="cifra">
          <span className="n">{datos.resumen.clientesConMemoria}</span>
          <span className="q">clientes con datos</span>
        </div>
        <div className="cifra">
          <span className="n">{datos.resumen.categorias}</span>
          <span className="q">categorías</span>
        </div>
        <div
          className={datos.resumen.porRevisar > 0 ? "cifra alerta" : "cifra"}
        >
          <span className="n">{datos.resumen.porRevisar}</span>
          <span className="q">por confirmar</span>
        </div>
        <div className={datos.resumen.caducadas > 0 ? "cifra alerta" : "cifra"}>
          <span className="n">{datos.resumen.caducadas}</span>
          <span className="q">caducadas</span>
        </div>
        <div
          className={
            datos.resumen.clientesSinNada > 0 ? "cifra alerta" : "cifra"
          }
        >
          <span className="n">{datos.resumen.clientesSinNada}</span>
          <span className="q">sin saber nada</span>
        </div>
      </div>

      {/* ── Las capas, como pestañas: es lo que se elige primero ── */}
      <nav className="capas">
        <button
          className={categoria === TODAS ? "capa activa" : "capa"}
          onClick={() => setCategoria(TODAS)}
        >
          Todo
          <span className="n">
            {[...porCategoria.values()].reduce((a, b) => a + b, 0)}
          </span>
        </button>

        <button
          className={categoria === TAREAS ? "capa activa" : "capa"}
          onClick={() => setCategoria(TAREAS)}
          title="Lo que se ha hecho cada mes, por cliente"
        >
          <span className="ico" aria-hidden="true">
            ✓
          </span>
          Tareas mensuales
        </button>

        {pestanas.map((a) => (
          <button
            key={a.slug}
            className={categoria === a.slug ? "capa activa" : "capa"}
            onClick={() => setCategoria(a.slug)}
            title={a.ayuda}
          >
            <span className="ico" aria-hidden="true">
              {ICONOS[a.slug] ?? "•"}
            </span>
            {a.nombre}
            <span className={porCategoria.get(a.slug) ? "n" : "n cero"}>
              {porCategoria.get(a.slug) ?? 0}
            </span>
          </button>
        ))}

        <button
          className="capa nueva"
          onClick={() => void crearCategoria()}
          disabled={ocupado}
          title="Una carpeta nueva: Competidores, Google Ads, Reuniones…"
        >
          + Nueva categoría
        </button>
      </nav>

      {/**
       * El SEO va JUSTO DEBAJO de las pestañas, no al final.
       *
       * Estuvo primero dentro del `aside` de 320 píxeles, donde se leía como
       * una tira de letras. Al sacarlo lo puse al final de la página — debajo
       * de la lista de recuerdos y de la barra lateral— y ahí pasó algo peor:
       * Jorge entró en SEO, vio la pantalla de siempre y me dijo que no había
       * cambiado nada. Tenía razón: había que bajar media pantalla para
       * encontrarlo, y lo que hay que bajar a buscar no existe.
       *
       * Ahora es lo primero que se ve al elegir un cliente, que es como está
       * en el diseño que él pidió.
       */}
      {elegido && categoria === SEO && elegido.ficha.grupo && !elegido.ficha.seRanking && !elegido.ficha.searchConsole && (
        <p className="aviso-grupo">
          El SEO de <strong>{elegido.nombre}</strong> se mide en{" "}
          <strong>{datos.clientes.find((o) => o.id === elegido.ficha.grupo)?.nombre ?? "su grupo"}</strong>, que es la web de
          todos.{" "}
          <button type="button" className="acc" onClick={() => setCliente(elegido.ficha.grupo!)}>
            Ver el SEO del grupo
          </button>
        </p>
      )}
      {elegido && categoria === SEO && (
        <SeoDelCliente
          abrirEn={pedido?.clienteId === elegido.id ? pedido.donde : undefined}
          clienteId={elegido.id}
          nombreCliente={elegido.nombre}
          ficha={elegido.ficha}
          quienEs={<QuienEs cliente={elegido} todos={datos.clientes} irA={setCliente} />}
          comoEstaConectado={<ComoEstaConectado cliente={elegido} />}
          tareas={
            <div className="dos-columnas">
              {listaDeRecuerdos(true)}
              {barraLateral}
            </div>
          }
        />
      )}

      {/**
       * Las tareas del mes ocupan el ancho entero.
       *
       * No son recuerdos con una columna de cliente al lado: son una tabla de
       * lo que se ha hecho, y se lee de izquierda a derecha.
       */}
      {categoria === PUBLICACIONES ? (
        <Publicaciones
          alElegir={(id) => {
            setCliente(id);
            setCategoria(SEO);
          }}
        />
      ) : categoria === TAREAS ? (
        <TrabajoDelMes cliente={elegido} />
      ) : categoria === SEO && elegido ? null : (
        /* ── Lo que sabe, y de quién ── */
        <div className="dos-columnas">
          {listaDeRecuerdos(false)}
          {barraLateral}
        </div>
      )}

      {(creando || editando) && (
        <Formulario
          memoria={editando}
          areas={datos.areas}
          clientes={datos.clientes}
          clientePorDefecto={elegido?.id ?? null}
          areaPorDefecto={categoria === TODAS ? null : categoria}
          cerrar={() => {
            setCreando(false);
            setEditando(null);
          }}
          guardado={() => {
            setCreando(false);
            setEditando(null);
            void cargar();
          }}
        />
      )}
    </div>
    </>
  );
}

interface Publicacion {
  clienteId: string;
  cliente: string;
  titulo: string;
  keyword: string | null;
  mes: string | null;
  estado: string;
  tipo: string | null;
  origen: string | null;
  apoyaA: string | null;
  porQue: string | null;
}

/**
 * Todo lo que hay que escribir, de todos los clientes, en una lista.
 *
 * Es la vista con la que se trabaja: quien redacta no abre 58 clientes para
 * ver qué le toca. Lo pendiente arriba y lo publicado al final, porque lo
 * primero es una cola de trabajo y lo segundo un histórico.
 *
 * **Los clientes SIN plan salen contados.** Un calendario editorial que sólo
 * enseña lo que hay se lee como si estuviera todo cubierto, y lo caro no es lo
 * que está mal planificado: es lo que no está planificado y nadie mira.
 */
function Publicaciones({
  alElegir,
}: {
  alElegir: (clienteId: string) => void;
}) {
  const [datos, setDatos] = useState<{
    publicaciones: Publicacion[];
    resumen: {
      previstos: number;
      escritos: number;
      publicados: number;
      clientesConPlan: number;
      clientesSinPlan: number;
    };
  } | null>(null);
  const [cargando, setCargando] = useState(true);
  const [verPublicados, setVerPublicados] = useState(false);

  useEffect(() => {
    void fetch("/api/memoria/publicaciones", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setDatos)
      .catch(() => setDatos(null))
      .finally(() => setCargando(false));
  }, []);

  if (cargando) return <p className="pista">Cargando el plan editorial…</p>;
  if (!datos) return <p className="pista">No se ha podido cargar.</p>;

  const lista = verPublicados
    ? datos.publicaciones
    : datos.publicaciones.filter((p) => p.estado !== "publicado");

  return (
    <section className="caja publicaciones">
      <header>
        <h2>Publicaciones</h2>
        <span className="de">
          · lo que hay que escribir, de toda la cartera
        </span>
      </header>

      <div className="kpis">
        <div className="kpi">
          <span className="que">Por escribir</span>
          <strong>{datos.resumen.previstos}</strong>
        </div>
        <div className="kpi">
          <span className="que">Escritos sin publicar</span>
          <strong>{datos.resumen.escritos}</strong>
        </div>
        <div className="kpi">
          <span className="que">Publicados</span>
          <strong>{datos.resumen.publicados}</strong>
        </div>
        <div className="kpi">
          <span className="que">Clientes con plan</span>
          <strong>{datos.resumen.clientesConPlan}</strong>
        </div>
        {/* Lo caro no es lo mal planificado: es lo que no está planificado. */}
        <div className="kpi">
          <span className="que">Clientes SIN plan</span>
          <strong className={datos.resumen.clientesSinPlan > 0 ? "peor" : ""}>
            {datos.resumen.clientesSinPlan}
          </strong>
        </div>
      </div>

      <label className="ver-publicados">
        <input
          type="checkbox"
          checked={verPublicados}
          onChange={(e) => setVerPublicados(e.target.checked)}
        />
        Enseñar también lo ya publicado
      </label>

      {lista.length === 0 ? (
        <p className="vacio2">
          Nada previsto. Se apunta dentro de cada cliente, en SEO →
          Publicaciones, y ahí BILBOBOT propone ideas sacadas de lo que ya se ha
          medido.
        </p>
      ) : (
        <div className="tabla-publicaciones">
          <div className="cabecera">
            <span>Cliente</span>
            <span>Qué escribir</span>
            <span>Para</span>
            <span>Mes</span>
            <span>Estado</span>
          </div>
          {lista.map((p, i) => (
            <div className="fila" key={`${p.clienteId}-${p.titulo}-${i}`}>
              <button
                type="button"
                className="quien"
                onClick={() => alElegir(p.clienteId)}
              >
                {p.cliente}
              </button>
              <span className="que">
                {p.titulo}
                {p.porQue && <span className="pista">{p.porQue}</span>}
              </span>
              <span className="para">
                {p.keyword ?? p.apoyaA ?? "—"}
                {p.tipo && <span className="pill">{p.tipo}</span>}
              </span>
              <span className="mes">{p.mes ?? "—"}</span>
              <span className={`estado ${p.estado}`}>{p.estado}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Un campo que se edita en el sitio.
 *
 * Se ve como texto hasta que se pulsa, y entonces es un cuadro. Un formulario
 * siempre abierto convierte una ficha de leer en una pantalla de rellenar, y
 * lo que más se hace con esto es mirarlo.
 *
 * Guarda al salir del campo o con Enter, y vuelve atrás con Escape.
 */
function Editable({
  que,
  valor,
  ayuda,
  alGuardar,
}: {
  que: string;
  valor: string;
  ayuda?: string;
  alGuardar: (nuevo: string) => Promise<string | null>;
}) {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(valor);
  const [fallo, setFallo] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    setTexto(valor);
  }, [valor]);

  async function guardar(): Promise<void> {
    if (texto.trim() === valor.trim()) {
      setEditando(false);
      return;
    }
    setOcupado(true);
    const error = await alGuardar(texto.trim());
    setOcupado(false);
    setFallo(error);
    if (!error) setEditando(false);
  }

  return (
    <div>
      <dt>{que}</dt>
      <dd>
        {editando ? (
          <>
            <input
              className="editable"
              value={texto}
              autoFocus
              disabled={ocupado}
              placeholder={ayuda}
              onChange={(e) => setTexto(e.target.value)}
              onBlur={() => void guardar()}
              onKeyDown={(e) => {
                if (e.key === "Enter") void guardar();
                if (e.key === "Escape") {
                  setTexto(valor);
                  setEditando(false);
                  setFallo(null);
                }
              }}
            />
            {fallo && <span className="pista peor">{fallo}</span>}
          </>
        ) : (
          <button
            type="button"
            className="como-texto"
            onClick={() => setEditando(true)}
          >
            {valor || <span className="sin">— añadir</span>}
          </button>
        )}
      </dd>
    </div>
  );
}

/** Guarda un trozo de la ficha. Devuelve el error, o null si fue bien. */
async function guardarFicha(
  clienteId: string,
  ruta: "ficha" | "conexiones",
  cuerpo: Record<string, unknown>,
): Promise<string | null> {
  const r = await fetch(`/api/memoria/clientes/${clienteId}/${ruta}`, {
    method: "PUT",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(cuerpo),
  });
  if (r.ok) return null;
  const j = (await r.json().catch(() => ({}))) as { error?: string };
  return j.error ?? "No se ha podido guardar.";
}

/**
 * QUIEN ES el cliente: la web, las demas webs, el correo y lo que paga.
 *
 * Estaba mezclado en la ficha lateral con los identificadores de Search
 * Console y Analytics, que no son el cliente sino la fontaneria. Son dos
 * preguntas distintas —«quien es este» y «como esta conectado»— y juntas se
 * leian como una lista de codigos.
 */
export function QuienEs({
  cliente,
  todos = [],
  irA,
}: {
  cliente: ClienteConFicha;
  /** Todos los clientes, para el grupo y sus sociedades. */
  todos?: ClienteConFicha[];
  /** Abrir otro cliente (el grupo, o una de sus sociedades). */
  irA?: (id: string) => void;
}) {
  const f = cliente.ficha;
  const sociedades = todos.filter((o) => o.ficha.grupo === cliente.id);
  const grupo = f.grupo ? todos.find((o) => o.id === f.grupo) ?? null : null;
  const deTodas = sociedades.reduce((suma, o) => suma + o.ficha.servicios.reduce((x, s) => x + (s.conIva ?? 0), 0), 0);
  const cuota = f.servicios.reduce((suma, s) => suma + (s.conIva ?? 0), 0);

  const vacia = !f.dominio && !f.email && f.servicios.length === 0;
  const guardar = (campo: string, valor: unknown) =>
    guardarFicha(cliente.id, "ficha", { [campo]: valor });
  // Las listas se escriben separadas por comas: es como se leen y como se
  // dictan. Un campo por dominio para once dominios seria una pantalla.
  const enLista = (t: string) =>
    t
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);

  return (
    <section className="caja-seo ficha-seo">
      <header>
        <h3>{cliente.nombre}</h3>
        {grupoDelCliente(serviciosConGrupo(cliente, todos)) === "mensual" ? (
          <span className="pill mejor" title="Paga cada mes SEO o mantenimiento">Importante · mensual</span>
        ) : grupoDelCliente(serviciosConGrupo(cliente, todos)) === "anual" ? (
          <span className="pill" title="Sólo paga el alojamiento, una vez al año">Anual · alojamiento</span>
        ) : null}
        <span className="pista">Quien es y que tiene contratado.</span>
      </header>

      {grupo && (
        <p className="aviso-grupo">
          Forma parte de <strong>{grupo.nombre}</strong>: es el mismo cliente y se gestiona junto con él
          {f.servicios.some((x) => x.conIva) ? ", aunque paga por separado" : ""}.{" "}
          {irA && (
            <button type="button" className="acc sec" onClick={() => irA(grupo.id)}>
              Ir a {grupo.nombre}
            </button>
          )}
        </p>
      )}
      {sociedades.length > 0 && (
        <div className="aviso-grupo">
          <p>
            Agrupa <strong>{sociedades.length}</strong> {sociedades.length === 1 ? "ficha más" : "fichas más"} del mismo cliente
            {deTodas > 0 && (
              <>
                {" "}— entre ellas pagan <strong>{deTodas.toLocaleString("es-ES", { maximumFractionDigits: 2 })} € al mes</strong> aparte
              </>
            )}
            :
          </p>
          <ul>
            {sociedades.map((o) => (
              <li key={o.id}>
                {irA ? (
                  <button type="button" className="enlace" onClick={() => irA(o.id)}>
                    {o.nombre}
                  </button>
                ) : (
                  o.nombre
                )}
                {" · "}
                {o.ficha.servicios
                  .map((s) => `${s.tipo} ${s.descripcion}${s.conIva ? ` ${s.conIva.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : ""}`)
                  .join(" · ") || "sin nada apuntado"}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Un cliente del que no hay ficha es informacion, no un hueco: son los
          43 que todavia no tienen dominio guardado, y por eso sus informes no
          se pueden atribuir a nadie. */}
      {vacia && (
        <p className="nota">
          No tiene ficha: ni web, ni servicios. Sin el dominio guardado, un
          informe suyo no se puede atribuir a nadie. Se rellena aqui mismo.
        </p>
      )}

      <dl className="detalle">
        <Editable
          que="Web"
          valor={f.dominio ?? ""}
          ayuda="ejemplo.com"
          alGuardar={(v) => guardar("dominio", v)}
        />
        {/* Las demas webs del mismo cliente. Se ensenan porque un informe que
            solo mira la principal deja fuera diez propiedades sin decirlo. */}
        <Editable
          que="Tambien"
          valor={(f.otrosDominios ?? []).join(", ")}
          ayuda="separadas por comas"
          alGuardar={(v) => guardar("otrosDominios", enLista(v))}
        />
        <Editable
          que="Correo"
          valor={f.email ?? ""}
          alGuardar={(v) => guardar("email", v)}
        />
        <Editable
          que="Tambien llamado"
          valor={f.alias.join(", ")}
          ayuda="separados por comas"
          alGuardar={(v) => guardar("alias", enLista(v))}
        />
      </dl>

      <Servicios cliente={cliente} cuota={cuota} />
    </section>
  );
}

/**
 * COMO ESTA CONECTADO: de donde salen los numeros y con que llaves se entra.
 *
 * Es lo que hay que mirar cuando una cifra no cuadra o falta una fuente, y
 * hasta ahora estaba escondido entre el correo y los servicios.
 */
export function ComoEstaConectado({ cliente }: { cliente: ClienteConFicha }) {
  const f = cliente.ficha;

  const guardar = (campo: string, valor: unknown) =>
    guardarFicha(cliente.id, "conexiones", { [campo]: valor });
  const sinConectar = !f.seRanking && !f.searchConsole && !f.analytics;

  return (
    <section className="caja-seo ficha-seo">
      <header>
        <h3>Configuracion</h3>
        <span className="pista">
          De donde salen los numeros y con que se entra.
        </span>
      </header>

      {sinConectar && (
        <p className="nota">
          Sin conectar a ninguna fuente. Sin Search Console ni Analytics ni SE
          Ranking, de este cliente no se puede medir nada: el panel saldra vacio
          y no es un fallo.
        </p>
      )}

      {/*
       * Un identificador mal puesto NO da error: da cifras de otro.
       *
       * Cinco clinicas del Grupo Scanner comparten propiedad de Analytics y
       * sus informes traian el trafico de las cinco. Por eso se corrige aqui,
       * que es donde se ve, y no hablando.
       */}
      <dl className="detalle">
        <Editable
          que="SEO"
          valor={f.estadoSeo ?? ""}
          ayuda="activo, pausado o no"
          alGuardar={(v) => guardar("estado", v)}
        />
        <Editable
          que="SE Ranking"
          valor={f.seRanking ?? ""}
          ayuda="nombre del proyecto"
          alGuardar={(v) => guardar("seRanking", v)}
        />
        <Editable
          que="Search Console"
          valor={f.searchConsole ?? ""}
          ayuda="https://ejemplo.com/"
          alGuardar={(v) => guardar("searchConsole", v)}
        />
        <Editable
          que="Analytics"
          valor={f.analytics ?? ""}
          ayuda="properties/123456789"
          alGuardar={(v) => guardar("analytics", v)}
        />
        {/* Vacío está bien: se busca solo en el robots.txt. Esto es para el
            que lo tenga en un sitio que no es ninguno de los habituales. */}
        <Editable
          que="Sitemap"
          valor={f.sitemap ?? ""}
          ayuda="se busca solo; ponlo si está en un sitio raro"
          alGuardar={(v) => guardar("sitemap", v)}
        />
      </dl>

      {f.keywords.length > 0 && (
        <div className="s">
          <strong>Keywords que sigue SE Ranking:</strong>
          {f.keywords.map((k) => (
            <span className="pill" key={k}>
              {k}
            </span>
          ))}
        </div>
      )}

      {/* Otras credenciales que el director anoto por su cuenta. Que existen y
          con que usuario: la contrasena vive en el baul y no sale de ahi ni
          para pintarla. */}
      <Accesos cliente={cliente} />

      <AccesoWeb cliente={cliente} />
    </section>
  );
}

interface Hecho {
  fecha: string;
  cliente: string;
  que: string;
  estado: "terminado" | "sin completar" | "en marcha";
  aMano: boolean;
}

/**
 * Lo que se ha hecho para este cliente, mes a mes.
 *
 * Es la pregunta que llega por teléfono —«¿qué habéis hecho este mes?»— y la
 * que hay que contestar al preparar la factura. Estaba sólo en el chat, y
 * obligar a abrir una conversación para verla convierte una consulta de dos
 * segundos en un turno de modelo.
 *
 * Se enseña el ESTADO de cada cosa, no sólo la lista. Un trabajo que falló y
 * uno terminado se ven igual en una lista pelada, y contárselo a un cliente
 * como hecho es la peor forma de usar esto.
 */
function TrabajoDelMes({ cliente }: { cliente: ClienteConFicha | null }) {
  const ahora = new Date();
  const [mes, setMes] = useState(
    `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, "0")}`,
  );
  const [hechos, setHechos] = useState<Hecho[] | null>(null);

  useEffect(() => {
    const [a, m] = mes.split("-").map(Number);
    const desde = `${mes}-01`;
    // Día 0 del mes siguiente es el último del actual, sin tablas de días.
    const hasta = new Date(Date.UTC(a!, m!, 0)).toISOString().slice(0, 10);
    setHechos(null);
    void fetch(
      `/api/memoria/clientes/${cliente?.id ?? "todos"}/trabajo?desde=${desde}&hasta=${hasta}`,
      {
        credentials: "same-origin",
      },
    )
      .then((r) => (r.ok ? r.json() : { hechos: [] }))
      .then((d: { hechos: Hecho[] }) => setHechos(d.hechos))
      .catch(() => setHechos([]));
  }, [cliente?.id, mes]);

  const terminados =
    hechos?.filter((h) => h.estado === "terminado").length ?? 0;

  return (
    <section className="caja trabajo-mes">
      {/**
       * Un solo encabezado, y el mes dentro de él.
       *
       * Había dos títulos —«Tareas mensuales» arriba y «Lo que se ha hecho»
       * debajo— diciendo lo mismo con distintas palabras. Dos títulos seguidos
       * no orientan: hacen dudar de si son dos cosas distintas.
       */}
      <header>
        <h2>
          Tareas mensuales
          {cliente && <span className="de"> · {cliente.nombre}</span>}
        </h2>
        <span className="mandos-capa">
          <input
            type="month"
            value={mes}
            onChange={(e) => setMes(e.target.value)}
          />
          {hechos && hechos.length > 0 && (
            <span className="pill">
              {terminados} terminado{terminados === 1 ? "" : "s"}
              {hechos.length > terminados
                ? ` · ${hechos.length - terminados} no`
                : ""}
            </span>
          )}
        </span>
      </header>

      {hechos === null ? null : hechos.length === 0 ? (
        <p className="nota">
          Nada apuntado este mes. Ojo: eso no es lo mismo que «no se ha hecho
          nada» — lo que no pasa por BILBOBOT no consta. Si hubo reuniones o
          cambios a mano, cuéntaselos al director y los apunta.
        </p>
      ) : (
        <ul className="hechos">
          {hechos.map((h, i) => (
            /* El estado lleva espacios —«en marcha»— y como clase eso son DOS
               clases. Se usa sólo si está terminado o no, que es lo que pinta. */
            <li
              key={`${h.fecha}-${i}`}
              className={h.estado === "terminado" ? "terminado" : "pendiente"}
            >
              <span className="cuando">{h.fecha.slice(8)}</span>
              {/* Sin cliente elegido hay que decir de quién es cada cosa. */}
              {!cliente && <span className="dequien">{h.cliente}</span>}
              <span className="que">{h.que}</span>
              {h.estado !== "terminado" && (
                <span className="pill mal">{h.estado}</span>
              )}
              {h.aMano && <span className="pill">a mano</span>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

interface Acceso {
  dominio: string;
  usuario: string | null;
  guardadoEl: string | null;
  usadoEl: string | null;
}

interface Prueba {
  ok: boolean;
  usuario: string | null;
  roles: string[];
  puedeEditar: boolean;
  detalle: string;
}

/**
 * El acceso de WordPress de este cliente.
 *
 * Está aquí, en su ficha, porque así es como se piensa: «abro La Darika y le
 * pongo su usuario». Pero es importante que se vea que **no es memoria**: lo
 * que se escribe en la memoria se le manda al modelo cuando trabaja para ese
 * cliente, y una contraseña ahí saldría por la API en el primer encargo. Esto
 * se guarda cifrado y el director sólo llega a ver una etiqueta.
 *
 * La contraseña no vuelve nunca del servidor. Ni para rellenar el formulario al
 * corregir el usuario, ni con un botón de «ver». Así, quien se haga con la
 * sesión del panel puede meter accesos —molesto, y se nota— pero no llevarse
 * los que ya hay, que es lo que de verdad haría daño.
 */
/**
 * Lo que tiene contratado, editable.
 *
 * La lista entera se guarda de una vez porque son tres o cuatro líneas y se
 * tocan dos veces al año: una ruta por fila sería mucha maquinaria para eso.
 */
function Servicios({
  cliente,
  cuota,
}: {
  cliente: ClienteConFicha;
  cuota: number;
}) {
  const [filas, setFilas] = useState(cliente.ficha.servicios);
  const [abierto, setAbierto] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    setFilas(cliente.ficha.servicios);
    setAbierto(false);
  }, [cliente.id, cliente.ficha.servicios]);

  async function guardar(siguientes: typeof filas): Promise<void> {
    setOcupado(true);
    const r = await fetch(`/api/memoria/clientes/${cliente.id}/servicios`, {
      method: "PUT",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ servicios: siguientes }),
    });
    setOcupado(false);
    setFallo(r.ok ? null : "No se ha podido guardar.");
  }

  return (
    <div className="s bloque-editable">
      <strong>Contratado:</strong>
      {!abierto ? (
        <>
          {filas.length === 0 ? (
            <span className="sin">nada anotado</span>
          ) : (
            <span>{filas.map((x) => x.descripcion || x.tipo).join(" · ")}</span>
          )}
          {cuota > 0 && (
            <span className="pill">{cuota.toFixed(2)} € con IVA</span>
          )}
          <button
            type="button"
            className="mini"
            onClick={() => setAbierto(true)}
          >
            editar
          </button>
        </>
      ) : (
        <div className="editor-filas">
          {filas.map((fila, i) => (
            <div className="fila-editable" key={i}>
              <input
                value={fila.descripcion || fila.tipo}
                placeholder="qué tiene contratado"
                onChange={(e) =>
                  setFilas(
                    filas.map((f, n) =>
                      n === i ? { ...f, descripcion: e.target.value } : f,
                    ),
                  )
                }
              />
              <input
                className="corto"
                type="number"
                step="0.01"
                value={fila.conIva ?? ""}
                placeholder="€/mes"
                onChange={(e) =>
                  setFilas(
                    filas.map((f, n) =>
                      // Vacío es «no lo sé», no cero: cero sumaría a la cartera
                      // como un cliente que no paga.
                      n === i
                        ? {
                            ...f,
                            conIva:
                              e.target.value === ""
                                ? null
                                : Number(e.target.value),
                          }
                        : f,
                    ),
                  )
                }
              />
              <button
                type="button"
                className="mini"
                title="Quitar"
                onClick={() => setFilas(filas.filter((_, n) => n !== i))}
              >
                ×
              </button>
            </div>
          ))}
          <div className="mandos-fila">
            <button
              type="button"
              className="mini"
              onClick={() =>
                setFilas([
                  ...filas,
                  { tipo: "", descripcion: "", conIva: null },
                ])
              }
            >
              + añadir
            </button>
            <button
              type="button"
              className="mini"
              disabled={ocupado}
              onClick={() => void guardar(filas).then(() => setAbierto(false))}
            >
              {ocupado ? "Guardando…" : "Guardar"}
            </button>
            <button
              type="button"
              className="mini"
              onClick={() => {
                setFilas(cliente.ficha.servicios);
                setAbierto(false);
                setFallo(null);
              }}
            >
              Cancelar
            </button>
          </div>
          {fallo && <span className="pista peor">{fallo}</span>}
        </div>
      )}
    </div>
  );
}

/**
 * Los accesos anotados: QUÉ hay y con qué usuario, nunca el valor.
 *
 * Y se dice si la referencia SIRVE. Antes ponía «contraseña en el baúl» de
 * todo lo anotado sin mirar, y las dos que había apuntaban a un sitio que el
 * lector real no sabe resolver: el baúl no tenía ninguna contraseña de web.
 * Decir que una contraseña está guardada cuando no lo está es peor que no
 * decir nada, porque se cuenta con ella.
 */
function Accesos({ cliente }: { cliente: ClienteConFicha }) {
  const [nombre, setNombre] = useState("");
  const [usuario, setUsuario] = useState("");
  const [referencia, setReferencia] = useState("");
  const [abierto, setAbierto] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const credenciales = cliente.ficha.credenciales;

  async function mandar(cuerpo: Record<string, unknown>): Promise<void> {
    setOcupado(true);
    const r = await fetch(`/api/memoria/clientes/${cliente.id}/accesos`, {
      method: "PUT",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(cuerpo),
    });
    setOcupado(false);
    if (r.ok) {
      setFallo(null);
      setAbierto(false);
      setNombre("");
      setUsuario("");
      setReferencia("");
      return;
    }
    const j = (await r.json().catch(() => ({}))) as { error?: string };
    setFallo(j.error ?? "No se ha podido guardar.");
  }

  return (
    <div className="s bloque-editable">
      <strong>Otros accesos anotados:</strong>
      {credenciales.length === 0 && <span className="sin">ninguno</span>}
      {credenciales.map((c) => (
        <span className="acceso" key={c.nombre}>
          {c.nombre}
          {c.usuario ? ` (${c.usuario})` : ""}
          {c.valida ? (
            <span className="pill ok">en el baúl</span>
          ) : (
            <span className="pill mal" title={c.referencia ?? "sin referencia"}>
              referencia rota
            </span>
          )}
          <button
            type="button"
            className="mini"
            title="Quitar la anotación"
            disabled={ocupado}
            onClick={() => void mandar({ nombre: c.nombre, quitar: true })}
          >
            ×
          </button>
        </span>
      ))}

      {!abierto ? (
        <button type="button" className="mini" onClick={() => setAbierto(true)}>
          + anotar acceso
        </button>
      ) : (
        <div className="editor-filas">
          <div className="fila-editable">
            <input
              value={nombre}
              placeholder="nombre (wp-admin)"
              onChange={(e) => setNombre(e.target.value)}
            />
            <input
              value={usuario}
              placeholder="usuario"
              onChange={(e) => setUsuario(e.target.value)}
            />
          </div>
          <input
            value={referencia}
            placeholder="vault://dominio.com/clave"
            onChange={(e) => setReferencia(e.target.value)}
          />
          {/*
           * Aquí NO va la contraseña.
           *
           * Va una referencia a dónde está. Una contraseña escrita aquí
           * acabaría en la base de datos y en el contexto del modelo, que es
           * justo lo que este sistema existe para evitar. El servidor la
           * rechaza aunque se pegue.
           */}
          <span className="pista">
            Sólo la referencia, nunca la contraseña. La contraseña se mete
            aparte con <code>npm run acceso-web</code>, que la cifra sin que la
            vea nadie.
          </span>
          <div className="mandos-fila">
            <button
              type="button"
              className="mini"
              disabled={ocupado}
              onClick={() => void mandar({ nombre, usuario, referencia })}
            >
              {ocupado ? "Guardando…" : "Anotar"}
            </button>
            <button
              type="button"
              className="mini"
              onClick={() => setAbierto(false)}
            >
              Cancelar
            </button>
          </div>
          {fallo && <span className="pista peor">{fallo}</span>}
        </div>
      )}
    </div>
  );
}

function AccesoWeb({ cliente }: { cliente: ClienteConFicha }) {
  const { preguntar, pedir, avisar, dialogo } = useDialogo();
  const dominioDelCliente = normalizar(cliente.ficha.dominio ?? "");
  const [acceso, setAcceso] = useState<Acceso | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [prueba, setPrueba] = useState<Prueba | null>(null);
  const [dominio, setDominio] = useState(dominioDelCliente);
  const [usuario, setUsuario] = useState("");
  const [clave, setClave] = useState("");

  async function mirar() {
    const r = await fetch("/api/accesos-web", { credentials: "same-origin" });
    if (!r.ok) return;
    const { accesos } = (await r.json()) as { accesos: Acceso[] };
    setAcceso(accesos.find((a) => a.dominio === dominioDelCliente) ?? null);
  }

  useEffect(() => {
    setAcceso(null);
    setPrueba(null);
    setAbierto(false);
    setDominio(dominioDelCliente);
    setUsuario("");
    setClave("");
    if (dominioDelCliente) void mirar();
    // Al cambiar de cliente se vuelve a mirar y se limpia lo tecleado: dejar
    // una contraseña a medio escribir en el formulario de OTRO cliente es la
    // forma más fácil de guardarla donde no va.
  }, [cliente.id]);

  async function guardar() {
    if (!dominio.trim() || !usuario.trim() || !clave) return;
    setOcupado(true);
    try {
      const r = await fetch("/api/accesos-web", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ dominio, usuario, clave }),
      });
      const cuerpo = (await r.json()) as { prueba?: Prueba; error?: string };
      if (!r.ok) {
        setPrueba({
          ok: false,
          usuario: null,
          roles: [],
          puedeEditar: false,
          detalle: cuerpo.error ?? "No se pudo guardar.",
        });
        return;
      }
      // La contraseña se borra de la pantalla en cuanto sale de aquí.
      setClave("");
      setAbierto(false);
      setPrueba(cuerpo.prueba ?? null);
      await mirar();
    } finally {
      setOcupado(false);
    }
  }

  async function probar() {
    setOcupado(true);
    setPrueba(null);
    try {
      const r = await fetch("/api/accesos-web/probar", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ dominio: acceso?.dominio ?? dominioDelCliente }),
      });
      if (r.ok) setPrueba(((await r.json()) as { prueba: Prueba }).prueba);
    } finally {
      setOcupado(false);
    }
  }

  async function quitar() {
    if (!acceso) return;
    if (
      !await preguntar(
        `¿Quitar el acceso de ${acceso.dominio}? Habrá que volver a meterlo entero.`,
      )
    )
      return;
    setOcupado(true);
    try {
      await fetch(`/api/accesos-web/${encodeURIComponent(acceso.dominio)}`, {
        method: "DELETE",
        credentials: "same-origin",
      });
      setPrueba(null);
      await mirar();
    } finally {
      setOcupado(false);
    }
  }

  return (
    <>
      {dialogo}
    <div className="acceso-web">
      <div className="cab">
        <strong>Acceso de WordPress</strong>
        {acceso ? (
          <span className="pill">
            {acceso.usuario ?? "sin usuario"} · guardado{" "}
            {cuando(acceso.guardadoEl ?? "")}
          </span>
        ) : (
          <span className="pill mal">sin acceso</span>
        )}
      </div>

      {!abierto && (
        <div className="mandos">
          <button
            className="acc sec"
            onClick={() => setAbierto(true)}
            disabled={ocupado}
          >
            {acceso ? "Cambiar" : "Añadir acceso"}
          </button>
          {acceso && (
            <>
              <button className="acc sec" onClick={probar} disabled={ocupado}>
                Probar
              </button>
              <button
                className="acc peligro"
                onClick={quitar}
                disabled={ocupado}
              >
                Quitar
              </button>
            </>
          )}
        </div>
      )}

      {abierto && (
        <div className="alta">
          <p className="nota">
            Hace falta un usuario de WordPress con rol <strong>Editor</strong> y
            una <strong>contraseña de aplicación</strong> suya (Usuarios → su
            perfil → Contraseñas de aplicación). La contraseña normal del panel
            no vale para esto, y no la guardes aquí.
          </p>
          <label className="campo">
            <span>Dominio</span>
            <input
              value={dominio}
              onChange={(e) => setDominio(e.target.value)}
              placeholder="darika.desarrollosweb.eu"
              autoComplete="off"
            />
          </label>
          <label className="campo">
            <span>Usuario de WordPress</span>
            <input
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
              placeholder="Bilbobot"
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <label className="campo">
            <span>Contraseña de aplicación</span>
            <input
              type="password"
              value={clave}
              onChange={(e) => setClave(e.target.value)}
              placeholder="abcd EFGH 1234 wxyz 5678 ijkl"
              /* Que el navegador no la guarde ni la ofrezca luego en otro
                 formulario: no es una contraseña de Jorge, es de una máquina. */
              autoComplete="new-password"
              spellCheck={false}
            />
          </label>
          <div className="mandos">
            <button
              className="acc"
              onClick={guardar}
              disabled={ocupado || !clave || !usuario}
            >
              {ocupado ? "Guardando…" : "Guardar y probar"}
            </button>
            <button
              className="acc sec"
              onClick={() => {
                setClave("");
                setAbierto(false);
              }}
              disabled={ocupado}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Guardar y funcionar son dos cosas distintas. Sin esto, la primera
          prueba de verdad era un encargo real fallando media hora después. */}
      {prueba && (
        <p
          className={
            prueba.ok && prueba.puedeEditar ? "veredicto bien" : "veredicto mal"
          }
        >
          {prueba.ok
            ? `Entra como «${prueba.usuario}»${prueba.roles.length ? ` (${prueba.roles.join(", ")})` : ""}. ${prueba.detalle}`
            : prueba.detalle}
        </p>
      )}
    </div>
    </>
  );
}

/** El mismo dominio escrito de cualquier forma, para poder compararlos. */
function normalizar(bruto: string): string {
  return bruto
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/[/:?#].*$/, "");
}

function Formulario({
  memoria,
  areas,
  clientes,
  /**
   * Dónde estabas mirando cuando pulsaste crear.
   *
   * Sin esto, estar en "SEO · ABOGANDER" y pulsar "Nueva memoria" abría un
   * formulario en blanco: había que volver a elegir el cliente entre 58 y la
   * categoría entre siete, para acabar donde ya estabas. Lo que se ve en
   * pantalla ES la intención.
   */
  clientePorDefecto,
  areaPorDefecto,
  cerrar,
  guardado,
}: {
  memoria: Memoria | null;
  areas: Area[];
  clientes: { id: string; nombre: string }[];
  clientePorDefecto?: string | null;
  areaPorDefecto?: string | null;
  cerrar: () => void;
  guardado: () => void;
}) {
  const [titulo, setTitulo] = useState(memoria?.titulo ?? "");
  const [contenido, setContenido] = useState(memoria?.contenido ?? "");
  const [tipo, setTipo] = useState(memoria?.tipo ?? "fact");
  // El área que ya tiene —puesta o deducida— para no perderla al corregir.
  const [area, setArea] = useState(
    memoria?.area ?? areaPorDefecto ?? "cliente",
  );
  const [clienteId, setClienteId] = useState(
    memoria?.clienteId ?? clientePorDefecto ?? "",
  );
  const [etiquetas, setEtiquetas] = useState(
    (memoria?.etiquetas ?? []).filter((e) => !e.startsWith("area:")).join(", "),
  );
  const [caduca, setCaduca] = useState(memoria?.caducaEn?.slice(0, 10) ?? "");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function guardar() {
    if (!titulo.trim() || !contenido.trim()) {
      setError("Hacen falta un título y un contenido.");
      return;
    }
    setEnviando(true);
    setError(null);

    const cuerpo = {
      titulo: titulo.trim(),
      contenido: contenido.trim(),
      tipo,
      clienteId: clienteId || null,
      area,
      /**
       * La categoría, además del área.
       *
       * El área es el nombre corto que usa el resto de la pantalla; la
       * categoría es la fila de la tabla. Se mandan las dos porque el área
       * sola no basta para MOVER: hasta que no hay `categoriaId`, la memoria
       * sigue colgando de donde estaba aunque la lista diga otra cosa.
       */
      categoriaId: areas.find((a) => a.slug === area)?.id ?? null,
      etiquetas: etiquetas
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean)
        .filter((e) => !e.startsWith("area:")),
      // Sin hora, la fecha se interpreta a mediodía para que no baile con la
      // zona horaria y caduque un día antes de lo que pusiste.
      caducaEn: caduca ? new Date(`${caduca}T12:00:00Z`).toISOString() : null,
    };

    const r = await fetch(
      memoria ? `/api/memoria/${memoria.id}` : "/api/memoria",
      {
        method: memoria ? "PATCH" : "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(cuerpo),
      },
    );

    setEnviando(false);
    if (!r.ok) {
      setError(
        ((await r.json()) as { error?: string }).error ?? "No se pudo guardar.",
      );
      return;
    }
    guardado();
  }

  return (
    <div className="capa-modal" onClick={cerrar}>
      <div className="ficha" onClick={(e) => e.stopPropagation()}>
        <h3>{memoria ? "Corregir lo que sabe" : "Enseñarle algo nuevo"}</h3>

        <label>
          En una frase
          <input
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="La Gilda del Norte paga a 30 días"
            autoFocus
          />
        </label>

        <label>
          Contado entero
          <textarea
            value={contenido}
            onChange={(e) => setContenido(e.target.value)}
            rows={5}
            placeholder="El detalle, y por qué importa. Esto es lo que leerá antes de decidir."
          />
        </label>

        <div className="dos">
          <label>
            De quién
            <select
              value={clienteId}
              onChange={(e) => setClienteId(e.target.value)}
            >
              <option value="">General (de nadie en concreto)</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </select>
          </label>

          <label>
            De qué va
            <select value={area} onChange={(e) => setArea(e.target.value)}>
              {areas.map((a) => (
                <option key={a.slug} value={a.slug}>
                  {a.nombre}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="dos">
          <label>
            Qué es
            <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
              <option value="fact">Un dato</option>
              <option value="preference">Una preferencia tuya</option>
              <option value="procedure">Cómo se hace algo</option>
              <option value="hypothesis">Una sospecha</option>
              <option value="episode">Algo que pasó</option>
            </select>
          </label>
        </div>

        <div className="dos">
          <label>
            Etiquetas
            <input
              value={etiquetas}
              onChange={(e) => setEtiquetas(e.target.value)}
              placeholder="facturación, seo"
            />
          </label>

          <label>
            Deja de valer el
            <input
              type="date"
              value={caduca}
              onChange={(e) => setCaduca(e.target.value)}
            />
          </label>
        </div>

        {/* Una fecha de caducidad es lo que impide que "el hosting vence en
            marzo" siga siendo cierto para el sistema en septiembre. */}
        <p className="nota">
          Si no pones fecha, lo dará por cierto para siempre. Ponla en todo lo
          que dependa de un contrato, un precio o una persona.
        </p>

        {error && <p className="mal">{error}</p>}

        <div className="botones">
          <button
            className="acc"
            onClick={() => void guardar()}
            disabled={enviando}
          >
            {memoria ? "Guardar" : "Que lo recuerde"}
          </button>
          <button className="acc sec" onClick={cerrar} disabled={enviando}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
